import Axios from "../../configs/axios.config";

// ==================== EMPLOYEE EXPENSE SUBMISSION ====================

/**
 * Submit new expense
 * POST /api/reimbursements
 */
export const createExpense = (data) =>
  Axios.post("/reimbursements", data).then((response) => response.data);

/**
 * Get employee's expenses list
 * GET /api/reimbursements/my-expenses
 */
export const getMyExpenses = (params) =>
  Axios.get("/reimbursements/my-expenses", { params }).then(
    (response) => response.data
  );

/**
 * Get expense detail
 * GET /api/reimbursements/{id}
 */
export const getExpenseDetail = (id) =>
  Axios.get(`/reimbursements/${id}`).then((response) => response.data);

/**
 * Update expense (before submission)
 * PUT /api/reimbursements/{id}
 */
export const updateExpense = (id, data) =>
  Axios.put(`/reimbursements/${id}`, data).then((response) => response.data);

/**
 * Delete expense (draft only)
 * DELETE /api/reimbursements/{id}
 */
export const deleteExpense = (id) =>
  Axios.delete(`/reimbursements/${id}`).then((response) => response.data);

/**
 * Submit expense for approval
 * POST /api/reimbursements/{id}/submit
 */
export const submitExpense = (id) =>
  Axios.post(`/reimbursements/${id}/submit`).then((response) => response.data);

// ==================== OCR FUNCTIONALITY ====================

/**
 * Upload receipt and extract data via OCR
 * POST /api/reimbursements/ocr-extract
 */
export const extractReceiptData = (formData) =>
  Axios.post("/reimbursements/ocr-extract", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  }).then((response) => response.data);

/**
 * Validate OCR extraction results
 * POST /api/reimbursements/validate-ocr
 */
export const validateOCRData = (data) =>
  Axios.post("/reimbursements/validate-ocr", data).then(
    (response) => response.data
  );

// ==================== MANAGER APPROVAL WORKFLOW ====================

/**
 * Get pending approvals (Manager view)
 * GET /api/reimbursements/pending-approvals
 */
export const getPendingApprovals = (params) =>
  Axios.get("/reimbursements/pending-approvals", { params }).then(
    (response) => response.data
  );

/**
 * Approve expense
 * POST /api/reimbursements/{id}/approve
 */
export const approveExpense = (id, data) =>
  Axios.post(`/reimbursements/${id}/approve`, data).then(
    (response) => response.data
  );

/**
 * Reject expense
 * POST /api/reimbursements/{id}/reject
 */
export const rejectExpense = (id, data) =>
  Axios.post(`/reimbursements/${id}/reject`, data).then(
    (response) => response.data
  );

/**
 * Return expense for clarification
 * POST /api/reimbursements/{id}/return
 */
export const returnExpense = (id, data) =>
  Axios.post(`/reimbursements/${id}/return`, data).then(
    (response) => response.data
  );

/**
 * Bulk approve expenses
 * POST /api/reimbursements/bulk-approve
 */
export const bulkApproveExpenses = (data) =>
  Axios.post("/reimbursements/bulk-approve", data).then(
    (response) => response.data
  );

// ==================== FINANCE PROCESSING ====================

/**
 * Get finance queue (all approved expenses)
 * GET /api/reimbursements/finance-queue
 */
export const getFinanceQueue = (params) =>
  Axios.get("/reimbursements/finance-queue", { params }).then(
    (response) => response.data
  );

/**
 * Process instant payout
 * POST /api/reimbursements/{id}/instant-payout
 */
export const processInstantPayout = (id, data) =>
  Axios.post(`/reimbursements/${id}/instant-payout`, data).then(
    (response) => response.data
  );

/**
 * Add to payroll
 * POST /api/reimbursements/{id}/add-to-payroll
 */
export const addToPayroll = (id, data) =>
  Axios.post(`/reimbursements/${id}/add-to-payroll`, data).then(
    (response) => response.data
  );

/**
 * Bulk add to payroll
 * POST /api/reimbursements/bulk-add-to-payroll
 */
export const bulkAddToPayroll = (data) =>
  Axios.post("/reimbursements/bulk-add-to-payroll", data).then(
    (response) => response.data
  );

/**
 * Mark as paid (manual confirmation)
 * POST /api/reimbursements/{id}/mark-paid
 */
export const markAsPaid = (id, data) =>
  Axios.post(`/reimbursements/${id}/mark-paid`, data).then(
    (response) => response.data
  );

// ==================== REPORTS & ANALYTICS ====================

/**
 * Get reimbursement summary/dashboard
 * GET /api/reimbursements/summary
 */
export const getReimbursementSummary = (params) =>
  Axios.get("/reimbursements/summary", { params }).then(
    (response) => response.data
  );

/**
 * Export expenses report
 * GET /api/reimbursements/export
 */
export const exportExpensesReport = (params) =>
  Axios.get("/reimbursements/export", {
    params,
    responseType: "blob",
  }).then((response) => response.data);

// ==================== EXPENSE CATEGORY MANAGEMENT ====================

/**
 * Get expense categories
 * GET /api/reimbursement-categories
 */
export const getExpenseCategories = () =>
  Axios.get("/reimbursement-categories").then((response) => response.data);

/**
 * Create expense category
 * POST /api/reimbursement-categories
 */
export const createExpenseCategory = (data) =>
  Axios.post("/reimbursement-categories", data).then(
    (response) => response.data
  );

/**
 * Update expense category
 * PUT /api/reimbursement-categories/{id}
 */
export const updateExpenseCategory = (id, data) =>
  Axios.put(`/reimbursement-categories/${id}`, data).then(
    (response) => response.data
  );

/**
 * Delete expense category
 * DELETE /api/reimbursement-categories/{id}
 */
export const deleteExpenseCategory = (id) =>
  Axios.delete(`/reimbursement-categories/${id}`).then(
    (response) => response.data
  );

/**
 * Get expense category detail
 * GET /api/reimbursement-categories/{id}
 */
export const getExpenseCategoryDetail = (id) =>
  Axios.get(`/reimbursement-categories/${id}`).then(
    (response) => response.data
  );

/**
 * Get expense policy limits
 * GET /api/reimbursement-policies
 */
export const getExpensePolicies = () =>
  Axios.get("/reimbursement-policies").then((response) => response.data);

/**
 * Check policy compliance
 * POST /api/reimbursements/check-policy
 */
export const checkPolicyCompliance = (data) =>
  Axios.post("/reimbursements/check-policy", data).then(
    (response) => response.data
  );

// ==================== PAYOUT PROVIDER INTEGRATION ====================

/**
 * Get payout provider status
 * GET /api/payout-provider/status
 * COMMENTED OUT - Not needed currently
 */
// export const getPayoutProviderStatus = () =>
//   Axios.get("/payout-provider/status").then((response) => response.data);

/**
 * Check employee KYC status for instant payout
 * GET /api/payout-provider/kyc-status/{employeeId}
 */
export const checkKYCStatus = (employeeId) =>
  Axios.get(`/payout-provider/kyc-status/${employeeId}`).then(
    (response) => response.data
  );

/**
 * Get payout transaction history
 * GET /api/payout-provider/transactions
 */
export const getPayoutTransactions = (params) =>
  Axios.get("/payout-provider/transactions", { params }).then(
    (response) => response.data
  );

// ==================== ACTIVITY LOG ====================

/**
 * Get expense activity log
 * GET /api/reimbursements/{id}/activity-log
 */
export const getExpenseActivityLog = (id) =>
  Axios.get(`/reimbursements/${id}/activity-log`).then(
    (response) => response.data
  );

// ==================== ALL EXPENSES (HR/Admin View) ====================

/**
 * Get all expenses (with filters)
 * GET /api/reimbursements
 */
export const getAllExpenses = (params) =>
  Axios.get("/reimbursements", { params }).then((response) => response.data);

